import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Grid,
  GridItem,
  VStack,
  IconButton,
  HStack,
  Circle,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { productLinks } from "../../constants/navigationData";
import { useNavigate } from "react-router-dom";

export default function OurProduct() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  
  const navigate = useNavigate();

  // Get background color based on product index
  const getProductScreenBg = useCallback((index) => {
    const colors = [
      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", 
      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
    ];
    return colors[index % colors.length];
  }, []);

  // Get main product images (different from logos)
  const getProductMainImage = useCallback((productId) => {
    const productImages = {
      'queuepro': '/png/queuepro_product_img.png',
      'ebs': '/png/EBS_product.png', 
      'insightdb': '/png/Insightdb_product.png',
      'ossp': '/png/ossp.png',
      'smart-delivery-platform': '/png/smart_deli_info.png',
      'digital-corporate-banking': '/png/DigitalCorporate.png'
    };
    return productImages[productId] || '/jpg/default-product.jpg';
  }, []);

  // Format static product data for the carousel
  const formatStaticProductData = useCallback((staticProducts) => {
    return staticProducts.map((product, index) => ({
      id: product.id,
      title: product.title,
      subtitle: product.subtitle,
      description: product.description,
      productLogo: product.image, // Use the logo for top-left corner
      productImage: getProductMainImage(product.id), // Get main product image
      screenBg: getProductScreenBg(index),
      href: product.href,
      isStatic: true
    }));
  }, [getProductScreenBg, getProductMainImage]);

  // Use static product data instead of API
  useEffect(() => {
    const formattedProducts = formatStaticProductData(productLinks);
    setAllProducts(formattedProducts);
    setLoading(false);
  }, [formatStaticProductData]);

  // Use static products
  const [allProducts, setAllProducts] = useState([]);
  const totalProducts = allProducts.length;

  // Auto-play functionality with hover pause
  useEffect(() => {
    if (totalProducts > 1 && !isHovered) { // Only enable auto-play if not hovered
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % totalProducts);
      }, 6000); // Slightly longer interval for better UX
      return () => clearInterval(interval);
    }
  }, [totalProducts, isHovered]);

  // Animation trigger with longer timeout
  useEffect(() => {
    setIsAnimating(true);
    const timeout = setTimeout(() => setIsAnimating(false), 750); // Extended timeout
    return () => clearTimeout(timeout);
  }, [currentIndex]);

  const handlePrev = () => {
    if (isAnimating) return; // Prevent rapid clicks during animation
    // setSlideDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? totalProducts - 1 : prev - 1));
  };

  const handleNext = () => {
    if (isAnimating) return; // Prevent rapid clicks during animation
    // setSlideDirection(1);
    setCurrentIndex((prev) => (prev + 1) % totalProducts);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentIndex) return; // Prevent unnecessary transitions
    // setSlideDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // Show loading state while fetching API data
  if (loading) {
    return (
      <Box bg="transparent" py={10} minH="100vh">
        <Container maxW="7xl" px={8}>
          <VStack spacing={6} textAlign="center" mb={16}>
            <Text fontSize={{ base: "xl", md: "2xl", lg: "4xl" }} fontWeight="bold">
              <Box as="span" color="#000B8C">Our</Box>{" "}
              <Box as="span" color="rgba(26, 26, 26, 1)">Products</Box>
            </Text>
          </VStack>
          <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
            <Spinner size="xl" color="#000B8C" />
          </Box>
        </Container>
      </Box>
    );
  }

  // Show message if no products available (shouldn't happen with static data)
  if (!loading && allProducts.length === 0) {
    return (
      <Box py={10} minH="100vh">
        <Container maxW="7xl" px={8}>
          <VStack spacing={6} textAlign="center" mb={16}>
            <Text fontSize={{ base: "xl", md: "2xl", lg: "4xl" }} fontWeight="bold">
              <Box as="span" color="#000B8C">Our</Box>{" "}
              <Box as="span" color="rgba(26, 26, 26, 1)">Products</Box>
            </Text>
          </VStack>
          <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
            <Text color="gray.500" fontSize="lg">
              No products available at the moment
            </Text>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box  py={10} minH="100vh">
      <Container maxW="7xl" px={8}>
        <VStack spacing={6} textAlign="center" mb={16}>
          <Text
            fontSize={{ base: "xl", md: "2xl", lg: "4xl" }}
            fontWeight="bold"
            lineHeight="shorter"
          >
            <Box as="span" color="#000B8C">
              Our
            </Box>{" "}
            <Box as="span" color="rgba(26, 26, 26, 1)">
              Products
            </Box>
          </Text>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="gray.600"
            maxW="4xl"
            lineHeight="relaxed"
          >
            Innovative, user-centric, and scalable — our products are designed
            to streamline operations, enhance customer experiences, and drive
            digital transformation across industries.
          </Text>
        </VStack>

        <Box position="relative">
          {/* Fade Container */}
          <Box
            bg="linear-gradient(135deg, #E6EFFF 0%, #C8D5FF 100%)"
            borderRadius="3xl"
            position="relative"
            overflow="hidden"
            maxH="700px"
            minH="500px"
            h="auto"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Products with Fade Animation */}
            {allProducts.map((product, index) => (
              <Box
                key={product.id}
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                p={{ base: 6, md: 8, lg: 10 }}
                opacity={currentIndex === index ? 1 : 0}
                visibility={currentIndex === index ? "visible" : "hidden"}
                transition="opacity 0.8s ease-in-out, visibility 0.8s ease-in-out"
                zIndex={currentIndex === index ? 2 : 1}
              >
                  <Grid
                    templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                    gap={{ base: 6, lg: 12 }}
                    alignItems="center"
                    h="full"
                    maxH="600px"
                  >
                    {/* Left Content */}
                    <GridItem>
                      <VStack
                        spacing={{ base: 3, md: 4, lg: 5 }}
                        align="flex-start"
                        h="full"
                        justify="center"
                      >
                        {/* Product Logo - positioned at top-left corner */}
                        <Box
                          position="relative"
                          w="full"
                        >
                          <Image
                            src={product.productLogo}
                            alt={`${product.title} logo`}
                            objectFit="contain"
                            w={{ base: "60px", md: "80px", lg: "100px" }}
                            h={{ base: "40px", md: "50px", lg: "60px" }}
                            maxW="150px"
                            fallback={
                              <Box
                                w={{ base: "60px", md: "80px", lg: "100px" }}
                                h={{ base: "40px", md: "50px", lg: "60px" }}
                                bg="gray.100"
                                borderRadius="md"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text color="gray.500" fontSize="xs" fontWeight="bold">
                                  LOGO
                                </Text>
                              </Box>
                            }
                          />
                        </Box>
                        
                        {/* Title */}
                        <Heading
                          as="h3"
                          fontSize={{ base: "xl", md: "2xl", lg: "4xl" }}
                          fontWeight="bold"
                          color="rgba(0, 11, 140, 1)"
                          lineHeight="tight"
                        >
                          {product.title}
                        </Heading>
                        
                        {/* Description */}
                        <Text
                          color="gray.700"
                          fontSize={{ base: "sm", md: "md", lg: "lg" }}
                          lineHeight="relaxed"
                          textAlign={"justify"}
                          maxW="500px"
                          noOfLines={{ base: 3, md: 4, lg: 6 }}
                        >
                          {product.description}
                        </Text>
                        
                        {/* Button */}
                        <Button
                          bg="white"
                          color="gray.800"
                          fontWeight="medium"
                          px={{ base: 6, md: 8 }}
                          py={{ base: 4, md: 6 }}
                          borderRadius="full"
                          border="1px"
                          borderColor="gray.300"
                          fontSize={{ base: "md", md: "lg" }}
                          cursor="pointer"
                          pointerEvents="auto"
                          zIndex={10}
                          position="relative"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent event bubbling
                            e.preventDefault();
                            // Navigate using static product href
                            navigate(product.href, {
                              state: {
                                title: product.title,
                                description: product.description,
                                productData: product,
                                isStatic: true
                              },
                            });
                          }}
                          _hover={{
                            bg: "gray.50",
                            shadow: "lg",
                            transform: "translateY(-2px)",
                            borderColor: "blue.300",
                          }}
                          _active={{
                            transform: "translateY(0px)",
                            bg: "gray.100",
                          }}
                        >
                          View Details
                        </Button>
                      </VStack>
                    </GridItem>
                    
                    {/* Right Content - Product Image */}
                    <GridItem>
                      <Box 
                        position="relative"
                      >
                        <Image
                          src={product.productImage}
                          alt={`${product.title} image`}
                          objectFit="contain"
                          w="full"
                          h={{ base: "250px", md: "350px", lg: "400px" }}
                          maxH="450px"
                          fallback={
                            <Box
                              bg={product.screenBg}
                              w="full"
                              h={{ base: "250px", md: "350px", lg: "400px" }}
                              maxH="450px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Text color="white" fontSize="xl" fontWeight="bold">
                                {product.title}
                              </Text>
                            </Box>
                          }
                        />
                       
                      </Box>
                    </GridItem>
                  </Grid>
                  
                  {/* Decorative Elements with subtle animations */}
                  <Box
                    position="absolute"
                    top="8"
                    right="8"
                    w="24"
                    h="24"
                    bg="whiteAlpha.200"
                    borderRadius="full"
                    filter="blur(20px)"
                    opacity={currentIndex === index ? 1 : 0.5}
                    transition="opacity 0.8s ease-out"
                  />
                  <Box
                    position="absolute"
                    bottom="8"
                    left="8"
                    w="32"
                    h="32"
                    bg="whiteAlpha.100"
                    borderRadius="full"
                    filter="blur(30px)"
                    opacity={currentIndex === index ? 1 : 0.3}
                    transition="opacity 1s ease-out"
                  />
                </Box>
              ))}
          </Box>

          {/* Navigation Arrows - only show if multiple products */}
          {totalProducts > 1 && (
            <>
              <IconButton
                aria-label="Previous product"
                icon={<ChevronLeftIcon boxSize={6} />}
                position="absolute"
                left="-6"
                top="50%"
                transform="translateY(-50%)"
                w="12"
                h="12"
                bg="white"
                border="2px"
                borderColor="gray.200"
                borderRadius="full"
                shadow="lg"
                _hover={{
                  bg: "gray.50",
                  borderColor: "blue.300",
                  transform: "translateY(-50%) scale(1.1)",
                  shadow: "xl",
                }}
                _active={{
                  transform: "translateY(-50%) scale(0.95)",
                }}
                transition="all 0.2s ease-out"
                onClick={handlePrev}
                disabled={isAnimating}
              />
              <IconButton
                aria-label="Next product"
                icon={<ChevronRightIcon boxSize={6} />}
                position="absolute"
                right="-6"
                top="50%"
                transform="translateY(-50%)"
                w="12"
                h="12"
                bg="white"
                border="2px"
                borderColor="gray.200"
                borderRadius="full"
                shadow="lg"
                _hover={{
                  bg: "gray.50",
                  borderColor: "blue.300",
                  transform: "translateY(-50%) scale(1.1)",
                  shadow: "xl",
                }}
                _active={{
                  transform: "translateY(-50%) scale(0.95)",
                }}
                transition="all 0.2s ease-out"
                onClick={handleNext}
                disabled={isAnimating}
              />
            </>
          )}
          
          {/* Carousel Indicators - only show if multiple products */}
          {totalProducts > 1 && (
            <HStack
              spacing={3}
              position="absolute"
              bottom="-12"
              left="50%"
              transform="translateX(-50%)"
              zIndex="4"
            >
              {allProducts.map((_, index) => (
                <Circle
                  key={index}
                  size="12px"
                  bg={currentIndex === index ? "#000B8C" : "gray.300"}
                  cursor="pointer"
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" // Smoother indicator transition
                  onClick={() => goToSlide(index)}
                  _hover={{
                    bg: currentIndex === index ? "#000B8C" : "gray.400",
                    transform: "scale(1.2)",
                  }}
                  _active={{
                    transform: "scale(0.9)",
                  }}
                />
              ))}
            </HStack>
          )}
        </Box>
      </Container>
    </Box>
  );
}
